<script setup lang="ts">

import type NewsItem from "@/types/NewsItem.ts";
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import NewsItemBaseStructure from '@/components/Sections/News/Types/Base/NewsItemBaseStructure.vue'
import NewsItemBase from '@/components/Sections/News/Types/Base/NewsItemBase.vue'
import { CDN } from '@/models/cdn.ts'
import SunkenMetricDisplay from '@/components/Elements/Display/SunkenMetricDisplay.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const assetStore = useAssetStore()

const props = defineProps<{
  newsItem: NewsItem,
  toast: boolean
}>();

const sizeName = computed(() => {
  if(props.newsItem.contentData.planetSize === 1) {
    return t('news.types.planetSettled.gravity.low')
  } else if(props.newsItem.contentData.planetSize === 2) {
    return t('news.types.planetSettled.gravity.medium')
  } else {
    return t('news.types.planetSettled.gravity.high')
  }
})

const typeName = computed(() => {
  return assetStore.findPlanetTypeById(props.newsItem.contentData.planetType)!.name
})

</script>

<template>
  <NewsItemBase
    :toast="toast"
    :toast-image="CDN.planet(64,props.newsItem.contentData.planetSize,props.newsItem.contentData.planetType)"
    :toast-text="t('news.types.planetSettled.title', { name: props.newsItem.contentData.planetName })"
  >
      <NewsItemBaseStructure
        :newsItem="props.newsItem"
        :item-image="CDN.planet(512,props.newsItem.contentData.planetSize,props.newsItem.contentData.planetType)"
        :header="t('news.types.planetSettled.title', { name: props.newsItem.contentData.planetName })"
        image-bg="bg-black"
      >
        <div class="flex flex-col justify-between h-full">
          <div class="flex-grow">{{ t('news.types.planetSettled.body', { name: props.newsItem.contentData.planetName }) }}</div>
          <div class="flex flex-row justify-end items-end gap-2 mt-1">
            <SunkenMetricDisplay v-if="props.newsItem.contentData.planetType !== 8">
              <span class="text-metal-400 text-xs mr-2">{{ t('news.types.planetSettled.gravity.label') }}</span>
              <span v-if="props.newsItem.contentData.planetSize === gameStore.player?.homeWorldPlanetSize" class="text-green-500">{{ sizeName }}</span>
              <span v-else-if="props.newsItem.contentData.planetSize === gameStore.player?.homeWorldOppositePlanetSize" class="text-red-500">{{ sizeName }}</span>
              <span v-else>{{ sizeName }}</span>
            </SunkenMetricDisplay>
            <SunkenMetricDisplay>
              <span class="text-metal-400 text-xs mr-2">{{ t('news.types.planetSettled.environment') }}</span>
              <span v-if="props.newsItem.contentData.planetType === gameStore.player?.homeWorldPlanetType" class="text-green-500">{{ typeName }}</span>
              <span v-else-if="props.newsItem.contentData.planetType === gameStore.player?.homeWorldOppositePlanetType" class="text-red-500">{{ typeName }}</span>
              <span v-else>{{ typeName }}</span>
            </SunkenMetricDisplay>
          </div>
        </div>
      </NewsItemBaseStructure>
  </NewsItemBase>
</template>

<style scoped lang="scss">

</style>